import React, {useEffect, useState} from "react";
import {Container, Row, Col, Button} from "reactstrap";
import {Tab, Tabs, TabList, TabPanel} from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import Loading from "../components/Loading";
import {useAuth0, withAuthenticationRequired} from "@auth0/auth0-react";
import Book from "../components/Book";
import BookSort from "../components/BookSort";
import BookFilter from "../components/BookFilter";
import chartData from "../services/chartData";
import BookModal from "../components/BookModal";
import {
    Alert, Avatar,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    InputAdornment,
    Fab, List, ListItem, ListItemAvatar,
    Snackbar, TextField,
    Tooltip, ListItemText, Switch, Stack, Divider
} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {filterRatingValueNice, filterValueNice, sortValueNice} from "../utils/filterHelper";
import {CircularProgressbar} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import {firebaseService} from "../services/firebaseService";
import {Dropdown} from "primereact/dropdown";
import OnlineTag from "../components/OnlineTag";
import {logEvent} from "firebase/analytics";
import {analytics} from "../firebase";
import {BarChart} from "@mui/x-charts";
import { RadarChart } from '@mui/x-charts/RadarChart';

export const ProfileComponent = () => {
    const [usersLibraryRaw, setUsersLibraryRaw] = useState([]); // Used to store the original library data
    const [usersLibrary, setUsersLibrary] = useState([]); // Used to display the library data
    const [usersLibraryLoaded, setUsersLibraryLoaded] = useState(false);
    const [sortValue, setSortValue] = useState("title asc");
    const [filterValue, setFilterValue] = useState("all");
    const [filterRatingValue, setFilterRatingValue] = useState(0);
    const [loadingLibrary, setLoadingLibrary] = useState(true);
    const [loadingUpdate, setLoadingUpdate] = useState(false);
    const [showBookModal, setShowBookModal] = useState(false);
    const [bookModalData, setBookModalData] = useState(null);
    const [bookModalNavValue, setBookModalNavValue] = useState("details");
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [openDrawer, setDrawer] = useState(false);
    const [bookModalEditMode, setBookModalEditMode] = useState(false);
    const [bookModalEditModeSubjects, setBookModalEditModeSubjects] = useState([]);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [bookToRemove, setBookToRemove] = useState(null);

    const [userDetails, setUserDetails] = useState([]);
    const [loadingFollowing, setLoadingFollowing] = useState(false);
    const [peopleSearchOptions, setPeopleSearchOptions] = useState([]);
    const [openUserSearchDrawer, setUserSearchDrawer] = useState(false);

    const [userInfoDialogOpen, setUserInfoDialogOpen] = useState(false);
    const [userInfoToShow, setUserInfoToShow] = useState(null);
    const [userInfoStatsToShow, setUserInfoStatsToShow] = useState(null);

    const {user} = useAuth0();

    // === LIBRARY === //
    const markAsReading = async (book, event) => {
        logEvent(analytics, 'book_updated_to_reading');
        setLoadingUpdate(true);
        const firebaseItem = usersLibraryRaw.find((item) => item.book.key === book.key);

        await firebaseService.updateBook(firebaseItem.id, {
            completed: false,
            currentlyReading: true,
            dateCompleted: null
        });
        setUsersLibrary(usersLibrary.map((item) => item.id === book.id ? {...item, completed: false} : item));
        setUsersLibraryRaw(usersLibraryRaw.map((item) => item.id === book.id ? {...item, completed: false} : item));
        setBookModalData({...bookModalData, completed: false, currentlyReading: true, dateCompleted: null});

        await firebaseService.addActivity(user, firebaseItem, "started reading");

        getLibrary().then((data) => {
            setUsersLibraryRaw(data);
            data = sortBy(sortValue, data);
            data = filterShow(filterValue, data);
            setUsersLibrary(data);
            setLoadingUpdate(false);
            setSnackbarMessage("Marked as reading");
            setShowSnackbar(true);
        });
    }

    const markAsNotReading = async (book, event) => {
        logEvent(analytics, 'book_updated_to_not_reading');
        setLoadingUpdate(true);
        const firebaseItem = usersLibraryRaw.find((item) => item.book.key === book.key);
        await firebaseService.updateBook(firebaseItem.id, {
            completed: false,
            currentlyReading: false,
            dateCompleted: null
        })
        setUsersLibrary(usersLibrary.map((item) => item.id === book.id ? {...item, completed: false} : item));
        setUsersLibraryRaw(usersLibraryRaw.map((item) => item.id === book.id ? {...item, completed: false} : item));
        setBookModalData({...bookModalData, currentlyReading: false, dateCompleted: null, completed: false});

        getLibrary().then((data) => {
            setUsersLibraryRaw(data);
            data = sortBy(sortValue, data);
            data = filterShow(filterValue, data);
            setUsersLibrary(data);
            setLoadingUpdate(false);
            setSnackbarMessage("Marked as not reading");
            setShowSnackbar(true);
        });
    }

    const markCompleted = async (book, event) => {
        logEvent(analytics, 'book_updated_to_completed');
        setLoadingUpdate(true);
        const firebaseItem = usersLibraryRaw.find((item) => item.book.key === book.key);
        await firebaseService.updateBook(firebaseItem.id, {
            completed: true,
            currentlyReading: false,
            dateCompleted: new Date()
        })
        setUsersLibrary(usersLibrary.map((item) => item.id === book.id ? {...item, completed: true} : item));
        setUsersLibraryRaw(usersLibraryRaw.map((item) => item.id === book.id ? {...item, completed: true} : item));
        setBookModalData({...bookModalData, completed: true, currentlyReading: false, dateCompleted: new Date()});

        await firebaseService.addActivity(user, firebaseItem, "completed");

        getLibrary().then((data) => {
            setUsersLibraryRaw(data);
            data = sortBy(sortValue, data);
            data = filterShow(filterValue, data);
            setUsersLibrary(data);
            setLoadingUpdate(false);
            setSnackbarMessage("Marked as completed");
            setShowSnackbar(true);
        });
    };

    const openConfirmDialog = (book) => {
        setConfirmDialogOpen(true);
        setBookToRemove(book);
    }

    const removeFromLibrary = async (book) => {
        logEvent(analytics, 'book_removed');
        setLoadingUpdate(true);

        const firebaseItem = usersLibraryRaw.find((item) => item.book.key === book.key);

        await firebaseService.deleteBook(firebaseItem.id);

        setBookToRemove(null);
        setConfirmDialogOpen(false)

        getLibrary().then((data) => {
            setUsersLibraryRaw(data);
            data = sortBy(sortValue, data);
            data = filterShow(filterValue, data);
            setUsersLibrary(data);
            setLoadingUpdate(false);
            setShowBookModal(false);
            setBookModalData(null)
            setBookModalNavValue("details");
            setSnackbarMessage("Book removed from library");
            setShowSnackbar(true);
        });
    }

    const getLibrary = async (loading = false) => {
        if (loading) {
            setLoadingLibrary(true);
        }

        setUsersLibraryLoaded(true);

        return await firebaseService.getUserLibrary(user);
    }

    const updateRating = async (book, value) => {
        logEvent(analytics, 'book_rating_updated');
        setLoadingUpdate(true);
        const firebaseItem = usersLibraryRaw.find((item) => item.book.key === book.key);
        await firebaseService.updateBook(firebaseItem.id, {
            rating: value,
        })
        setUsersLibrary(usersLibrary.map((item) => item.id === book.id ? {...item, rating: value} : item));
        setUsersLibraryRaw(usersLibraryRaw.map((item) => item.id === book.id ? {...item, rating: value} : item));
        setBookModalData({...bookModalData, rating: value});

        if (value > 0) {
            await firebaseService.addActivity(user, firebaseItem, "rated", value);
        }

        getLibrary().then((data) => {
            setUsersLibraryRaw(data);
            data = sortBy(sortValue, data);
            data = filterShow(filterValue, data);
            setUsersLibrary(data);
            setLoadingUpdate(false);
            setSnackbarMessage("Rating updated");
            setShowSnackbar(true);
        });
    }

    const saveBookUpdates = async (book) => {
        logEvent(analytics, 'book_edited_by_user');
        setLoadingUpdate(true);
        const firebaseItem = usersLibraryRaw.find((item) => item.book.key === book.book.key);
        await firebaseService.updateBook(firebaseItem.id, {
            book: book.book,
            coverData: book.tempCoverData ? book.tempCoverData : book.coverData,
        })
        book.coverData = book.tempCoverData ? book.tempCoverData : book.coverData;
        setLoadingUpdate(false);
        setSnackbarMessage("Updated book details");
        setShowSnackbar(true);
    }

    const recommendBook = async (book) => {
        logEvent(analytics, 'book_recommended_by_user');
        setLoadingUpdate(true);
        const firebaseItem = usersLibraryRaw.find((item) => item.book.key === book.key);
        await firebaseService.addActivity(user, firebaseItem, "recommends");
        setLoadingUpdate(false);
        setSnackbarMessage("Successfully recommended book");
        setShowSnackbar(true);
    }


    // === USER === //
    const getUserDetails = async (user, loading = false) => {
        if (loading) {
            setLoadingFollowing(true);
        }

        let details = await firebaseService.getUserDetails(user);

        if (details.length === 0) {
            await firebaseService.addUser(user);
            details = await firebaseService.getUserDetails(user);
        } else {
            let lastOnline = details.lastOnline.toDate();
            let now = new Date();
            let diff = now.getTime() - lastOnline.getTime();

            let diffMinutes = Math.round(diff / 60000);

            if (diffMinutes > 5) {
                await firebaseService.updateUser(details, {
                    lastOnline: new Date()
                });
            }
        }

        return details;
    }

    const searchForUsers = async (value) => {
        if (value.length > 1) {
            let users = await firebaseService.searchUsersByEmail(value, user);
            setPeopleSearchOptions(users);
        } else {
            setPeopleSearchOptions([]);
        }
    }

    const followUser = async (personToFollow) => {
        let data = {
            id: personToFollow.id,
            name: personToFollow.name,
            email: personToFollow.email,
            avatarURL: personToFollow.avatarURL
        };
        let dataString = JSON.stringify(data);
        let currentlyFollowing = (userDetails.following.length > 0) ? userDetails.following : [];
        currentlyFollowing.push(dataString)

        await firebaseService.updateUser(userDetails, {
            following: currentlyFollowing
        })

        getUserDetails(user, true).then((data) => {
            setLoadingFollowing(false);
            setUserDetails(data);
        });
    }

    const unfollowUser = async (personToUnfollow) => {
        let currentlyFollowing = (userDetails.following.length > 0) ? userDetails.following : [];
        currentlyFollowing = currentlyFollowing.filter(item => {
            let currentItem = getPersonObject(item);
            return currentItem.email !== personToUnfollow.email;
        })

        await firebaseService.updateUser(userDetails, {
            following: currentlyFollowing
        })

        getUserDetails(user, true).then((data) => {
            setLoadingFollowing(false);
            setUserDetails(data);
        });
    }

    const isFollowing = (person) => {
        let result = false;
        let currentlyFollowing = (userDetails.following.length > 0) ? userDetails.following : [];
        currentlyFollowing.forEach((personString) => {
            let personObject = getPersonObject(personString);
            if (personObject.email === person.email) {
                result = true;
            }
        })
        return result;
    }

    const getPersonObject = (personJSONString) => {
        return JSON.parse(personJSONString);
    }

    const openUserInfoDialog = async (userEmail) => {
        setUserInfoDialogOpen(true);
        setUserInfoStatsToShow(null);
        setUserInfoToShow(null);

        let userDetails = await firebaseService.searchUsersByEmail(userEmail, user, true);

        if (userDetails.length === 1) {
            let user = userDetails[0];
            setUserInfoToShow(user);

            let usersLibrary = await firebaseService.getUserLibrary(user);
            let numberOfBooks = usersLibrary.length;
            let numberOfCompletedBooks = usersLibrary.filter((book) => book.completed).length;
            let numberOfBooksReading = usersLibrary.filter((book) => book.currentlyReading).length;
            let numberOfBooksUnread = usersLibrary.filter((book) => !book.completed && !book.currentlyReading).length;

            setTimeout(() => {
                setUserInfoStatsToShow({
                    numberOfBooks: numberOfBooks.toString(),
                    numberOfCompletedBooks: numberOfCompletedBooks.toString(),
                    numberOfBooksReading: numberOfBooksReading.toString(),
                    numberOfBooksUnread: numberOfBooksUnread.toString()
                });
            }, 1000)
        }
    }

    const followsMe = (person) => {
        let followsMe = false;
        let following = person.following ? person.following : [];
        if (following.length > 0) {
            following.forEach((item) => {
                let i = JSON.parse(item);
                if (i.email === user.email) {
                    followsMe = true;
                }
            })
        }
        return followsMe;
    }


    // === SETTINGS === //
    const updateAccountPrivacy = async (isChecked) => {
        setLoadingUpdate(true);
        await firebaseService.updateUser(userDetails, {
            privateAccount: isChecked
        });
        getUserDetails(user, true).then((data) => {
            setLoadingFollowing(false);
            setLoadingUpdate(false);
            setUserDetails(data);
        });
    }

    const updateAccountDefaultSort = async (value) => {
        setLoadingUpdate(true);

        console.log(value)

        if (!value) {
            setLoadingFollowing(false);
            setLoadingUpdate(false);
            return;
        }

        await firebaseService.updateUser(userDetails, {
            defaultSort: value
        });
        getUserDetails(user, true).then((data) => {
            setLoadingFollowing(false);
            setLoadingUpdate(false);
            setUserDetails(data);
        });
    }

    const updateAccountDefaultFilter = async (value) => {
        setLoadingUpdate(true);

        if (!value) {
            setLoadingFollowing(false);
            setLoadingUpdate(false);
            return;
        }

        await firebaseService.updateUser(userDetails, {
            defaultFilter: value
        });
        getUserDetails(user, true).then((data) => {
            setLoadingFollowing(false);
            setLoadingUpdate(false);
            setUserDetails(data);
        });
    }

    // === FUNCTION & CONTROL === //
    useEffect(() => {
        if (user && !usersLibraryLoaded) {
            getUserDetails(user, true).then((userData) => {
                setLoadingFollowing(false);
                setUserDetails(userData);

                getLibrary(true).then((libraryData) => {
                    setLoadingLibrary(false);
                    setUsersLibraryRaw(libraryData);
                    let sort = userData.defaultSort ?? sortValue;
                    libraryData = sortBy(sort, libraryData);

                    let filter = userData.defaultFilter ?? filterValue;
                    libraryData = filterShow(filter, libraryData);

                    setUsersLibrary(libraryData);
                });

            });
        }
    })

    const sortBy = (value, applyToThis = null) => {
        setSortValue(value);

        let sortedLibrary;
        if (!applyToThis) {
            sortedLibrary = usersLibraryRaw;
        } else {
            sortedLibrary = applyToThis;
        }

        switch (value) {
            case "title asc":
                sortedLibrary.sort((a, b) => {
                    return a.book.title.localeCompare(b.book.title);
                });
                break;
            case "title desc":
                sortedLibrary.sort((a, b) => {
                    return b.book.title.localeCompare(a.book.title);
                });
                break;
            case "date asc":
                sortedLibrary.sort((a, b) => {
                    return a.dateAdded.seconds - b.dateAdded.seconds;
                });
                break;
            case "date desc":
                sortedLibrary.sort((a, b) => {
                    return b.dateAdded.seconds - a.dateAdded.seconds;
                });
                break;
            case "published asc":
                sortedLibrary.sort((a, b) => {
                    return a.book.first_publish_year - b.book.first_publish_year;
                });
                break;
            case "published desc":
                sortedLibrary.sort((a, b) => {
                    return b.book.first_publish_year - a.book.first_publish_year;
                });
                break;
            case "read asc":
                sortedLibrary.sort((a, b) => {
                    let aDate = a.dateCompleted ? a.dateCompleted.seconds : 9999999999;
                    let bDate = b.dateCompleted ? b.dateCompleted.seconds : 9999999999;
                    return aDate - bDate;
                });
                break;
            case "read desc":
                sortedLibrary.sort((a, b) => {
                    let aDate = a.dateCompleted ? a.dateCompleted.seconds : 0;
                    let bDate = b.dateCompleted ? b.dateCompleted.seconds : 0;
                    return bDate - aDate;
                });
                break;
            default:
                break
        }

        if (applyToThis) {
            return sortedLibrary;
        } else {
            sortedLibrary = filterShow(filterValue, sortedLibrary);
            sortedLibrary = filterByRating(filterRatingValue, sortedLibrary);
            setUsersLibrary(sortedLibrary);
        }
    }

    const filterShow = (value, applyToThis = null) => {
        setFilterValue(value);

        let filteredLibrary;
        if (!applyToThis) {
            filteredLibrary = usersLibraryRaw;
        } else {
            filteredLibrary = applyToThis;
        }

        switch (value) {
            case "completed":
                filteredLibrary = filteredLibrary.filter((book) => book.completed);
                break;
            case "reading":
                filteredLibrary = filteredLibrary.filter((book) => book.currentlyReading);
                break;
            case "unread":
                filteredLibrary = filteredLibrary.filter((book) => !book.completed && !book.currentlyReading);
                break;
            default:
                break;
        }

        if (applyToThis) {
            return filteredLibrary;
        } else {
            filteredLibrary = sortBy(sortValue, filteredLibrary);
            filteredLibrary = filterByRating(filterRatingValue, filteredLibrary);
            setUsersLibrary(filteredLibrary);
        }
    }

    const filterByRating = (value, applyToThis = null) => {
        setFilterRatingValue(value);

        let filteredLibrary;
        if (!applyToThis) {
            filteredLibrary = usersLibraryRaw;
        } else {
            filteredLibrary = applyToThis;
        }

        if (value !== 0) {
            filteredLibrary = filteredLibrary.filter((book) => book.rating === value);
        }

        if (applyToThis) {
            return filteredLibrary;
        } else {
            filteredLibrary = sortBy(sortValue, filteredLibrary);
            filteredLibrary = filterShow(filterValue, filteredLibrary);
            setUsersLibrary(filteredLibrary);
        }
    }

    const openBookModal = (book) => {
        setBookModalData(book);
        setShowBookModal(true);
    }

    const closeBookModal = () => {
        setShowBookModal(false);
        setBookModalData(null);
        setBookModalEditMode(false);
    }

    const singularOrPlural = (word, count) => {
        return (count === 1) ? word : word + "s";
    }

    const getNiceDate = (date) => {
        let dateStr = "";
        const dateObj = new Date(date.toDate());
        const now = new Date();
        const diff = now.getTime() - dateObj.getTime();
        const diffMinutes = Math.round(diff / 60000);

        if (diffMinutes < 1) {
            return "Just now";
        } else if (diffMinutes === 1) {
            return diffMinutes + " minute ago";
        } else if (diffMinutes < 60) {
            return diffMinutes + " minutes ago";
        }

        const dateOptions = {year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "numeric"};
        dateStr = dateObj.toLocaleDateString("en-US", dateOptions);
        return dateStr;
    }

    const getTabDefaultIndex = () => {
        if (window.location.href.includes("#following")) {
            return 2;
        }
        if (window.location.href.includes("#settings")) {
            return 3;
        }
        return 0;
    }

    const numberOfBooks = (loadingLibrary) ? <Loading/> : usersLibraryRaw.length;

    const numberOfCompletedBooks = (loadingLibrary) ?
        <Loading/> : usersLibraryRaw.filter((book) => book.completed).length;

    const numberOfBooksReading = (loadingLibrary) ?
        <Loading/> : usersLibraryRaw.filter((book) => book.currentlyReading).length;

    const numberOfBooksUnread = (loadingLibrary) ?
        <Loading/> : usersLibraryRaw.filter((book) => !book.completed && !book.currentlyReading).length;

    const charting = chartData(usersLibraryRaw);

    return (
        <>
            <NavBar/>
            <Container className="flex-grow-1 mt-5">
                <Container className="mb-5">

                    <Row className="align-items-center profile-header mb-5 text-center text-md-left">
                        <Col md={2}>
                            <Avatar
                                src={user.picture}
                                alt={user.name}
                                className="profile-picture"
                            />
                        </Col>
                        <Col md>
                            <div className="account-is-private">
                                {(userDetails.privateAccount) &&
                                    <FontAwesomeIcon icon="lock"/>
                                }
                            </div>
                            <h2>{user.name}</h2>
                            <p className="lead text-muted">{user.email}</p>
                        </Col>
                        <Col md className="profile-stats-container">
                            <div className="profile-stat">
                                <CircularProgressbar
                                    className="library-progress"
                                    value={(loadingLibrary) ? 0 : charting.percentageOfLibraryRead}
                                    text={<tspan
                                        dy={3}>{(loadingLibrary) ? "0%" : charting.percentageOfLibraryRead + "%"}</tspan>}
                                    strokeWidth="10"
                                />
                                <span>Library completed</span>
                            </div>

                            <div className="profile-stat">
                                <Tooltip title="In your library">
                                    <Chip icon={<FontAwesomeIcon icon="box-open"/>} label={numberOfBooks}
                                          className="chip total-chip"/>
                                </Tooltip>
                                <Tooltip title="Unread">
                                    <Chip icon={<FontAwesomeIcon icon="book"/>} label={numberOfBooksUnread}
                                          className="chip unread-chip"/>
                                </Tooltip>
                                <Tooltip title="Reading">
                                    <Chip icon={<FontAwesomeIcon icon="book-open"/>} label={numberOfBooksReading}
                                          className="chip reading-chip"/>
                                </Tooltip>
                                <Tooltip title="Completed">
                                    <Chip icon={<FontAwesomeIcon icon="check"/>} label={numberOfCompletedBooks}
                                          className="chip completed-chip"/>
                                </Tooltip>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Tabs style={{width: '100%'}} defaultIndex={getTabDefaultIndex()}>
                            <TabList>
                                <Tab>My Library</Tab>
                                <Tab>My Stats <Chip label={"beta"} className="chip beta-chip"/></Tab>
                                <Tab>Following</Tab>
                                <Tab>Settings</Tab>
                            </TabList>

                            <TabPanel>
                                <Row className="align-items-center mb-5 text-center text-md-left">
                                    <Col md>
                                        <div className="view-controls">
                                            {!loadingLibrary &&
                                                <>
                                                    <div className="drawer-link" onClick={() => {
                                                        setDrawer(true)
                                                    }}>
                                                        <FontAwesomeIcon icon="sliders-h"/>
                                                        <span>Sort and Filter</span>
                                                    </div>

                                                    <div className="active-filters">
                                                        <Chip label={sortValueNice(sortValue)} className="chip"/>

                                                        {filterValue !== "all" &&
                                                            <Chip label={filterValueNice(filterValue)} className="chip"
                                                                  deleteIcon={<FontAwesomeIcon icon="times"/>}
                                                                  onDelete={() => {
                                                                      filterShow("all")
                                                                  }}
                                                            />
                                                        }
                                                        {filterRatingValue !== 0 &&
                                                            <Chip icon={<FontAwesomeIcon icon="star"/>}
                                                                  label={filterRatingValueNice(filterRatingValue)}
                                                                  className="chip"
                                                                  deleteIcon={<FontAwesomeIcon icon="times"/>}
                                                                  onDelete={() => {
                                                                      filterByRating(0)
                                                                  }}
                                                            />
                                                        }
                                                    </div>
                                                </>
                                            }
                                        </div>

                                        {loadingLibrary &&
                                            <Loading/>
                                        }

                                        {(usersLibrary.length === 0 && !loadingLibrary) && (
                                            <div className="books-container empty">
                                                <h5>Your library is empty</h5>
                                                <Button color="primary" onClick={() => {
                                                    window.location.href = "/search"
                                                }}>Search and add books</Button>
                                            </div>
                                        )}

                                        {usersLibrary.length > 0 && (
                                            <>
                                                <div className="books-container">
                                                    {usersLibrary.map((book) => (
                                                        <Book
                                                            key={book.id}
                                                            book={book}
                                                            openBookModal={openBookModal}
                                                        />
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </Col>
                                </Row>
                            </TabPanel>

                            <TabPanel>
                                <Row className="align-items-center mb-5 text-center text-md-left">
                                    <Col md>
                                        <div className="chart-container">
                                            <div className="chart-section">
                                                <div className="chart-title">Books Read by Year</div>
                                                {charting.booksReadByYear.length > 0 ? (
                                                    <div className="chart-content">

                                                        <BarChart
                                                            series={[
                                                                {
                                                                    data: charting.booksReadByYear.map((item) => item.count),
                                                                    color: '#46addf'
                                                                }
                                                            ]}
                                                            barLabel={(item) => {
                                                                return item.value?.toString() + " books";
                                                            }}
                                                            height={350}
                                                            margin={{left: 0}}
                                                            xAxis={[{
                                                                scaleType: 'band',
                                                                data: charting.booksReadByYear.map((item) => item.year),
                                                            }]}
                                                            borderRadius={10}
                                                            axisHighlight={{
                                                                x: 'none',
                                                                y: 'none',
                                                            }}
                                                        />

                                                    </div>
                                                ) : (
                                                    <p className="no-data">No completed books to display stats for.</p>
                                                )}
                                            </div>

                                            <div className="chart-section">
                                                <div className="chart-title">Top 5 Genres</div>
                                                {charting.topGenres.length > 0 ? (
                                                    <div className="chart-content">
                                                        <RadarChart
                                                            height={400}
                                                            series={[{
                                                                data: charting.topGenres.map((item) => item.count),
                                                                fillArea: true,
                                                                color: '#46addf',
                                                            }]}
                                                            radar={{
                                                                metrics: charting.topGenres.map((item) => item.genre),
                                                                max: Math.max(...charting.topGenres.map((item) => item.count)),
                                                            }}
                                                            highlight={"none"}
                                                        />
                                                    </div>
                                                ) : (
                                                    <p className="no-data">No genres to display stats for.</p>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </TabPanel>

                            <TabPanel>
                                <Row className="following-container align-items-center mb-5 text-center text-md-left">
                                    <Col md className="column">

                                        {loadingFollowing &&
                                            <div className="loading-update-container">
                                                <Loading/>
                                            </div>
                                        }

                                        <div className="search-users-button">
                                            <Button onClick={() => {
                                                setUserSearchDrawer(true)
                                            }}>
                                                <FontAwesomeIcon icon="search"/>
                                                Search for people
                                            </Button>
                                        </div>

                                        <div className="following-section">
                                            {(userDetails.following?.length === 0 && !loadingFollowing) &&
                                                <p className="no-follow">You are not following anyone</p>
                                            }
                                            {userDetails.following?.length > 0 &&
                                                <List className="users-following">
                                                    {userDetails.following.map((person, key) => {
                                                        let personObject = getPersonObject(person);
                                                        return (
                                                            <ListItem key={key} className="user">
                                                                <ListItemAvatar>
                                                                    <OnlineTag person={personObject}/>
                                                                    <Avatar src={personObject.avatarURL}
                                                                            alt={personObject.name}/>
                                                                </ListItemAvatar>
                                                                <ListItemText
                                                                    className="main-text"
                                                                    primary={personObject.name}
                                                                    secondary={`— ${personObject.email}`}
                                                                    onClick={() => openUserInfoDialog(personObject.email)}
                                                                />
                                                                <ListItemText
                                                                    className="unfollow-button"
                                                                    primary={
                                                                        <Button onClick={() => {
                                                                            unfollowUser(personObject)
                                                                        }}>
                                                                            <FontAwesomeIcon icon="user-minus"
                                                                                             className="unfollow-icon"/>
                                                                        </Button>
                                                                    }
                                                                />
                                                            </ListItem>
                                                        )
                                                    })}
                                                </List>
                                            }
                                        </div>
                                    </Col>
                                </Row>
                            </TabPanel>

                            <TabPanel>
                                <div className="settings-container">
                                    <Col md className="column">
                                        <div className="settings-control">

                                            <div className="info input-fields">
                                                <div className="input-field member-since">
                                                    <span className="label">Member since</span>
                                                    <span className="value">
                                                        {userDetails.memberSince &&
                                                            getNiceDate(userDetails.memberSince)}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="input-fields">
                                                <div className="input-field private-account-toggle">
                                                    <span className="label">Private account</span>
                                                    <span className="action-details">Switch to a private account to hide yourself from the followers search</span>
                                                    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                                        <span><FontAwesomeIcon icon="lock-open"/></span>
                                                        <Switch
                                                            checked={userDetails.privateAccount ?? false}
                                                            onChange={(e) => {
                                                                updateAccountPrivacy(e.target.checked)
                                                            }}/>
                                                        <span><FontAwesomeIcon icon="lock"/></span>
                                                    </Stack>
                                                </div>
                                            </div>

                                            <div className="input-fields">
                                                <div className="input-field default-library-sorting">
                                                    <span className="label">Library sorting</span>
                                                    <span
                                                        className="action-details">Set your library's default sort</span>
                                                    <Dropdown value={userDetails.defaultSort ?? 'title asc'}
                                                              onChange={(e) => {
                                                                  updateAccountDefaultSort(e.value)
                                                              }}
                                                              options={[
                                                                  {name: 'Title A-Z', code: 'title asc'},
                                                                  {name: 'Title Z-A', code: 'title desc'},
                                                                  {name: 'Date Added (oldest first)', code: 'date asc'},
                                                                  {
                                                                      name: 'Date Added (newest first)',
                                                                      code: 'date desc'
                                                                  },
                                                                  {
                                                                      name: 'Published (oldest first)',
                                                                      code: 'published asc'
                                                                  },
                                                                  {
                                                                      name: 'Published (newest first)',
                                                                      code: 'published desc'
                                                                  },
                                                                  {name: 'Read (oldest first)', code: 'read asc'},
                                                                  {name: 'Read (newest first)', code: 'read desc'},
                                                              ]}
                                                              optionLabel="name"
                                                              optionValue="code"
                                                    />
                                                </div>
                                            </div>

                                            <div className="input-fields">
                                                <div className="input-field default-library-sorting">
                                                    <span className="label">Library filtering</span>
                                                    <span
                                                        className="action-details">Set your library's default filter status</span>
                                                    <Dropdown value={userDetails.defaultFilter ?? 'all'}
                                                              onChange={(e) => {
                                                                  updateAccountDefaultFilter(e.value)
                                                              }}
                                                              options={[
                                                                  {name: 'All', code: 'all'},
                                                                  {name: 'Completed', code: 'completed'},
                                                                  {name: 'Reading', code: 'reading'},
                                                                  {name: 'Unread', code: 'unread'},
                                                              ]}
                                                              optionLabel="name"
                                                              optionValue="code"
                                                    />
                                                </div>
                                            </div>

                                            <div className="input-fields danger-zone">
                                                <div className="zone-title">Danger zone</div>
                                                <div className="input-field empty-library">
                                                    <span className="label">Empty my library</span>
                                                    <span
                                                        className="action-details">This action can not be undone</span>
                                                    <Button disabled={true} onClick={() => {
                                                        console.log("empty library")
                                                    }}>
                                                        Empty my library
                                                    </Button>
                                                </div>
                                                <Divider/>
                                                <div className="input-field delete-account">
                                                    <span className="label">Delete my account</span>
                                                    <span
                                                        className="action-details">This action can not be undone</span>
                                                    <Button disabled={true} onClick={() => {
                                                        console.log("delete account")
                                                    }}>
                                                        Delete my account
                                                    </Button>
                                                </div>
                                            </div>

                                        </div>
                                    </Col>
                                </div>
                            </TabPanel>

                        </Tabs>
                    </Row>

                    {loadingUpdate &&
                        <div className="loading-update-container">
                            <Loading/>
                        </div>
                    }

                    <Drawer className="user-search-drawer" open={openUserSearchDrawer} onClose={() => {
                        setUserSearchDrawer(false)
                        setPeopleSearchOptions([])
                    }}>
                        <div className="drawer-inner">
                            <div className="close-button">
                                <FontAwesomeIcon icon="times" onClick={() => {
                                    setUserSearchDrawer(false)
                                    setPeopleSearchOptions([])
                                }}/>
                            </div>

                            <div className="input-wrapper">
                                <div className="search-section">
                                    <TextField label="Search for people" variant="outlined"
                                               className="follow-search-field"
                                               slotProps={{
                                                   input: {
                                                       startAdornment: (
                                                           <InputAdornment position="start">
                                                               <FontAwesomeIcon icon="search"/>
                                                           </InputAdornment>
                                                       ),
                                                   },
                                               }}
                                               onChange={(e) => {
                                                   searchForUsers(e.target.value);
                                               }}
                                    />
                                    <div className="user-search-results-container">
                                        {peopleSearchOptions.length > 0 &&
                                            <List className="user-search-results">
                                                {peopleSearchOptions.map((person) => {
                                                    let followsMe = false;
                                                    let following = person.following ? person.following : [];
                                                    if (following.length > 0) {
                                                        following.forEach((item) => {
                                                            let i = JSON.parse(item);
                                                            if (i.email === user.email) {
                                                                followsMe = true;
                                                            }
                                                        })
                                                    }
                                                    return (
                                                        <ListItem key={person.id}>
                                                            <ListItemAvatar>
                                                                <Avatar src={person.avatarURL} alt={person.name}/>
                                                            </ListItemAvatar>
                                                            <ListItemText
                                                                className="main-text"
                                                                primary={person.name}
                                                                secondary={`— ${person.email}`}
                                                            />
                                                            {followsMe &&
                                                                <Chip label="Follows you"
                                                                      className="follows-user-badge"/>
                                                            }
                                                            {(isFollowing(person)) ?
                                                                <ListItemText
                                                                    className="unfollow-button"
                                                                    primary={
                                                                        <Button onClick={() => {
                                                                            unfollowUser(person)
                                                                        }}>
                                                                            <FontAwesomeIcon icon="user-minus"
                                                                                             className="unfollow-icon"/>
                                                                        </Button>
                                                                    }
                                                                />
                                                                :
                                                                <ListItemText
                                                                    className="follow-button"
                                                                    primary={
                                                                        <Button onClick={() => {
                                                                            followUser(person)
                                                                        }}>
                                                                            <FontAwesomeIcon icon="user-plus"
                                                                                             className="follow-icon"/>
                                                                        </Button>
                                                                    }
                                                                />
                                                            }
                                                        </ListItem>
                                                    )
                                                })}
                                            </List>
                                        }
                                    </div>
                                </div>
                            </div>

                            <div className="done-button" onClick={() => {
                                setUserSearchDrawer(false)
                                setPeopleSearchOptions([])
                            }}>
                                <Fab variant="extended" color="primary" className="fab">
                                    Close
                                </Fab>
                            </div>
                        </div>
                    </Drawer>

                    <Drawer className="filter-drawer" open={openDrawer} onClose={() => {
                        setDrawer(false)
                    }}>
                        <div className="drawer-inner">
                            <div className="close-button">
                                <FontAwesomeIcon icon="times" onClick={() => {
                                    setDrawer(false)
                                }}/>
                            </div>

                            <div className="input-wrapper">
                                <BookSort sortBy={sortBy} sortValue={sortValue} filterValue={filterValue}/>

                                <BookFilter
                                    filterShow={filterShow}
                                    filterByRating={filterByRating}
                                    filterValue={filterValue}
                                    filterRatingValue={filterRatingValue}
                                    allCount={usersLibraryRaw.length}
                                    unreadCount={numberOfBooksUnread}
                                    readingCount={numberOfBooksReading}
                                    completedCount={numberOfCompletedBooks}
                                />
                            </div>

                            <div className="done-button" onClick={() => {
                                setDrawer(false)
                            }}>
                                <Fab variant="extended" color="primary" className="fab">
                                    <FontAwesomeIcon icon="check"/> Apply and
                                    show {usersLibrary.length} {singularOrPlural("book", usersLibrary.length)}
                                </Fab>
                            </div>
                        </div>
                    </Drawer>

                    <BookModal
                        showBookModal={showBookModal}
                        closeBookModal={closeBookModal}
                        book={bookModalData}
                        loadingUpdate={loadingUpdate}
                        bookModalEditMode={bookModalEditMode}
                        bookModalEditModeSubjects={bookModalEditModeSubjects}
                        setBookModalEditMode={setBookModalEditMode}
                        bookModalNavValue={bookModalNavValue}
                        setBookModalNavValue={setBookModalNavValue}
                        markAsReading={markAsReading}
                        markAsNotReading={markAsNotReading}
                        markCompleted={markCompleted}
                        openConfirmDialog={openConfirmDialog}
                        updateRating={updateRating}
                        saveBookUpdates={saveBookUpdates}
                        setBookModalEditModeSubjects={setBookModalEditModeSubjects}
                        recommendBook={recommendBook}
                    />

                    <Snackbar
                        open={showSnackbar}
                        onClose={() => setShowSnackbar(false)}
                        autoHideDuration={3000}
                        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                    >
                        <Alert
                            onClose={() => setShowSnackbar(false)}
                            severity="success"
                            variant="filled"
                            sx={{width: '100%'}}
                        >
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>

                </Container>
            </Container>

            <Dialog fullWidth className="user-info-dialog" open={userInfoDialogOpen} onClose={() => {
                setUserInfoDialogOpen(false)
                setUserInfoStatsToShow(null)
                setTimeout(() => {
                    setUserInfoToShow(null);
                }, 200)
            }}>
                {(userInfoToShow) ?
                    <div className="user-info-dialog-inner">
                        <DialogTitle>
                            <Avatar src={userInfoToShow.avatarURL} alt={userInfoToShow.name} className="user-avatar"/>
                            <div className="name-and-email">
                                <span className="user-name">
                                    {userInfoToShow.name}
                                    {followsMe(userInfoToShow) &&
                                        <Chip label="Follows you" className="follows-user-badge"/>
                                    }
                                </span>
                                <span className="user-email">{userInfoToShow.email}</span>
                            </div>
                        </DialogTitle>
                        <DialogContent>
                            <div className="inner">
                                <div className="info last-online">
                                    <span className="label">Last online</span>
                                    <span className="value">{getNiceDate(userInfoToShow.lastOnline)}</span>
                                </div>
                                <div className="info member-since">
                                    <span className="label">Member since</span>
                                    <span className="value">{getNiceDate(userInfoToShow.memberSince)}</span>
                                </div>


                                <div className="profile-stats">
                                    <Tooltip title="In their library">
                                        <Chip icon={<FontAwesomeIcon icon="box-open"/>}
                                              label={(userInfoStatsToShow?.numberOfBooks) ? userInfoStatsToShow.numberOfBooks :
                                                  <Loading/>}
                                              className="chip total-chip"/>
                                    </Tooltip>
                                    <Tooltip title="Unread">
                                        <Chip icon={<FontAwesomeIcon icon="book"/>}
                                              label={(userInfoStatsToShow?.numberOfBooksUnread) ? userInfoStatsToShow.numberOfBooksUnread :
                                                  <Loading/>}
                                              className="chip unread-chip"/>
                                    </Tooltip>
                                    <Tooltip title="Reading">
                                        <Chip icon={<FontAwesomeIcon icon="book-open"/>}
                                              label={(userInfoStatsToShow?.numberOfBooksReading) ? userInfoStatsToShow.numberOfBooksReading :
                                                  <Loading/>}
                                              className="chip reading-chip"/>
                                    </Tooltip>
                                    <Tooltip title="Completed">
                                        <Chip icon={<FontAwesomeIcon icon="check"/>}
                                              label={(userInfoStatsToShow?.numberOfCompletedBooks) ? userInfoStatsToShow.numberOfCompletedBooks :
                                                  <Loading/>}
                                              className="chip completed-chip"/>
                                    </Tooltip>
                                </div>


                            </div>
                        </DialogContent>
                        <DialogActions className="dialog-actions">
                            <Button className="dialog-button" color="primary" onClick={() => {
                                setUserInfoDialogOpen(false)
                                setUserInfoToShow(null)
                            }}>Close</Button>
                        </DialogActions>
                    </div>
                    : <Loading/>
                }
            </Dialog>

            <Dialog fullWidth className="confirm-remove-dialog" open={confirmDialogOpen} onClose={() => {
                setConfirmDialogOpen(false)
            }}>
                {bookToRemove &&
                    <>
                        <DialogTitle>Warning</DialogTitle>
                        <DialogContent>Are you sure you want to remove {bookToRemove.title}?</DialogContent>
                        <DialogActions className="dialog-actions">
                            <Button className="dialog-button" color="warning" onClick={() => {
                                removeFromLibrary(bookToRemove)
                            }}>Yes</Button>
                            <Button className="dialog-button" color="primary" onClick={() => {
                                setConfirmDialogOpen(false)
                            }}>No</Button>
                        </DialogActions>
                    </>
                }
            </Dialog>

            <Footer/>
        </>

    );
};

export default withAuthenticationRequired(ProfileComponent, {
    onRedirecting: () => <Loading/>,
});
